// --- Dependencies -------------------------------------------------------------------------------
import { useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useLang from "@/hooks/useLang";
import useOrganization from "@/hooks/useOrganization";
import useTranslation from "@/hooks/useTranslation";
import { readLangValue } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import type { TyMembership } from "@/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = { onClose: () => void };

/**
 * The company picker. It opens under the badge with an invisible cover across the whole screen, so a
 * click anywhere dismisses it — a dropdown that only closes when you hit its own button is a trap.
 *
 * Choosing a different company sends you home. Everything else refreshes on its own: `useFetch` reruns
 * whenever the active organization changes, so no screen can keep showing the previous company's data.
 */
export default function SwitchOrganizationPopup({ onClose }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const memberships = useAuth((state) => state.memberships);
    const organizationId = useOrganization((state) => state.organizationId);
    const setOrganization = useOrganization((state) => state.setOrganization);

    const handleSelect = (membership: TyMembership) => {
        onClose();
        if (membership.organization._id === organizationId) return;
        setOrganization(membership.organization._id, membership.role);
        navigate("/");
    };

    return (
        <section className="popup-card switch-org">
            <h2 className="switch-org__title">{t.auth.selectOrganization}</h2>

            <div className="switch-org__list">
                {memberships.map((membership) => {
                    const name = readLangValue(membership.organization.name, lang);
                    const isActive = membership.organization._id === organizationId;

                    return (
                        <button
                            key={membership.organization._id}
                            type="button"
                            className={`switch-org__item${isActive ? " switch-org__item--active" : ""}`}
                            onClick={() => handleSelect(membership)}
                        >
                            <Avatar name={name} picture={membership.organization.logo} size={34} bordered />
                            <span className="switch-org__text">
                                <span className="switch-org__name">{name}</span>
                                <span className="switch-org__role">{t.roles[membership.role]}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </section>
    );
}
